/**
 * Which explainer a cycle's step gets, worked out the same way on every
 * surface.
 *
 * The treatment hub explains the step tab she selected; Home explains the step
 * she is on. Both build the explainer's context here, from the same step list,
 * so the hub's current tab and Home cannot explain one step two ways.
 */

import {
  resolveCycleCurrentStepId,
  type CurrentStepInput,
} from "@src/packages/cycles/hooks/resolveCycleHeaderState";
import type { CycleType } from "@src/packages/cycles/hooks/useCycles";
import {
  getStepsForCycle,
  type ConditionalCycleOptions,
  type StepPerspective,
  type TreatmentStepConfig,
} from "@src/packages/homepage/sections/treatmentFlows";
import type { TranslationFunction } from "@src/packages/i18n/translations";
import { applyFrozenOocyteFlowRewrites } from "../cryovault/frozenCycleSelection";
import type { StepExplainerContext } from "./stepExplainers";

type TriggerPhase = { triggerShot?: unknown };

/** The steps a trigger shot ends, before the egg retrieval. */
const STEPS_BEFORE_RETRIEVAL = new Set([
  "protocol",
  "stimulation",
  "trigger_shot",
]);

/**
 * The steps the treatment hub shows for a cycle: the flow for the cycle type
 * and perspective, without the collection step when an IUI already has a
 * frozen sample linked, and rewritten for frozen eggs (no retrieval; the
 * stimulation step prepares the lining).
 */
export function getHubStepsForCycle({
  cycleType,
  perspective,
  t,
  phases,
  pgtPlan,
  cyclePlanSnapshot,
  hasLinkedIuiSpecimen,
  usingFrozenOocytes,
}: {
  cycleType: CycleType;
  perspective: StepPerspective;
  t: TranslationFunction;
  phases: ConditionalCycleOptions["phases"];
  pgtPlan?: ConditionalCycleOptions["pgtPlan"];
  cyclePlanSnapshot?: ConditionalCycleOptions["cyclePlanSnapshot"];
  hasLinkedIuiSpecimen: boolean;
  usingFrozenOocytes: boolean;
}): TreatmentStepConfig[] {
  const base = getStepsForCycle(cycleType, perspective, t, {
    phases,
    pgtPlan,
    cyclePlanSnapshot,
  });
  const cycleSteps =
    cycleType === "iui" && hasLinkedIuiSpecimen
      ? base.filter((step) => step.id !== "sperm_collection")
      : base;
  return applyFrozenOocyteFlowRewrites(
    cycleSteps,
    cycleType,
    usingFrozenOocytes,
    t,
  );
}

/**
 * Whether this cycle has a trigger shot, planned or recorded: a stimulation
 * step that includes one, or a trigger logged on any phase (a frozen transfer
 * only has one when it is recorded). The waits read it to say that an hCG
 * trigger can show on an early home test, and leave that out otherwise.
 */
export function cycleHadTriggerShot(
  steps: readonly Pick<TreatmentStepConfig, "hasTriggerShot">[],
  phases: readonly TriggerPhase[],
): boolean {
  return (
    steps.some((step) => step.hasTriggerShot === true) ||
    phases.some((phase) => phase.triggerShot != null)
  );
}

/** The explainer context for one step of a cycle, as the hub's tab reads it. */
export function stepExplainerContextForStep({
  step,
  cycleType,
  perspective,
  usingFrozenOocytes,
  cycleHadTriggerShot,
}: {
  step: Pick<TreatmentStepConfig, "id" | "hasTriggerShot">;
  cycleType: CycleType;
  perspective: StepPerspective;
  usingFrozenOocytes: boolean;
  cycleHadTriggerShot: boolean;
}): StepExplainerContext {
  return {
    stepId: step.id,
    cycleType,
    perspective,
    usingFrozenOocytes,
    hasTriggerShot: step.hasTriggerShot === true,
    cycleHadTriggerShot,
  };
}

/**
 * The explainer context for the step she is on: the step the cycle header
 * treats as current (`resolveCycleCurrentStepId`), read from the hub's own
 * step list for her cycle. Null when there is no step to explain.
 */
export function resolveCurrentStepExplainerContext({
  t,
  cycle,
  phases,
  perspective,
  usingFrozenOocytes,
  now,
}: {
  t: TranslationFunction;
  cycle: CurrentStepInput["cycle"];
  phases: CurrentStepInput["phases"];
  perspective: StepPerspective;
  usingFrozenOocytes: boolean;
  now: Date;
}): StepExplainerContext | null {
  if (!cycle) return null;
  const steps = getHubStepsForCycle({
    cycleType: cycle.cycleType,
    perspective,
    t,
    phases,
    cyclePlanSnapshot: cycle.cyclePlanSnapshot,
    // Home does not read the IUI sample link; the cycle's own plan already
    // leaves the collection out when the clinic planned a frozen sample.
    hasLinkedIuiSpecimen: false,
    usingFrozenOocytes,
  });
  const stepId = resolveCycleCurrentStepId({
    cycle,
    phases,
    cyclePlanSnapshot: cycle.cyclePlanSnapshot,
    now,
    shownStepIds: steps.map((step) => step.id),
  });
  // Once the trigger is in, she is getting ready for the retrieval: when to
  // stop eating and drinking, and someone to take her home. Home says that,
  // as it did before the explainers were shared. The hub keeps stimulation as
  // its current tab until that step is closed; its retrieval tab carries this
  // same explainer.
  const triggered =
    stepId !== null &&
    STEPS_BEFORE_RETRIEVAL.has(stepId) &&
    steps.some((candidate) => candidate.id === "egg_retrieval") &&
    phases.some((phase) => phase.triggerShot != null);
  const explainedStepId = triggered ? "egg_retrieval" : stepId;
  const step = explainedStepId
    ? steps.find((candidate) => candidate.id === explainedStepId)
    : null;
  if (!step) return null;
  return stepExplainerContextForStep({
    step,
    cycleType: cycle.cycleType,
    perspective,
    usingFrozenOocytes,
    cycleHadTriggerShot: cycleHadTriggerShot(steps, phases),
  });
}
